import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useModal } from '../../hooks/useModal';
import { recordService, referenceService, quotationService } from '../../services/api';

const RecordManagement = ({ user }) => {
  // Permissions
  const canManageRecords = ['Administrator', 'Manager', 'Employee'].includes(user?.role);
  const canDelete = ['Administrator', 'Manager'].includes(user?.role);
  const [records, setRecords] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');

  useModal(showModal || showEditModal || showViewModal || showChangelogModal, () => {
    setShowModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setShowChangelogModal(false);
  });
  const [filterOrg, setFilterOrg] = useState('');
  const [formData, setFormData] = useState({
    date: '',
    organization: '',
    project: '',
    quotation: '',
    author: '',
    type: 'Letter',
    attachment: [], // Changed to array for multiple files
    linkType: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [recRes, orgRes, projRes, quoteRes] = await Promise.all([
        recordService.getAll(),
        referenceService.getOrgs(),
        referenceService.getProjects(),
        quotationService.getAll()
      ]);
      setRecords(recRes.data);
      setOrganizations(orgRes.data);
      setProjects(projRes.data);
      setQuotations(quoteRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      Swal.fire('Error', 'Failed to load records', 'error');
      setLoading(false);
    }
  };

  const loadRecords = async () => {
    try {
      const response = await recordService.getAll();
      setRecords(response.data);
    } catch (error) {
      console.error("Error loading records:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let uploadedAttachments = [];

      // If there are new files to upload
      const newFiles = Array.isArray(formData.attachment) ? formData.attachment.filter(f => f instanceof File) : [];
      if (newFiles.length > 0) {
        const uploadData = new FormData();
        newFiles.forEach(file => uploadData.append('files', file));
        const uploadRes = await recordService.uploadFiles(uploadData);
        uploadedAttachments = uploadRes.data;
      }

      // Existing attachments (already strings/objects from backend)
      const existingAttachments = Array.isArray(formData.attachment) ? formData.attachment.filter(f => !(f instanceof File)) : [];

      const allAttachments = [...existingAttachments, ...uploadedAttachments];

      // Prepare payload
      const payload = {
        ...formData,
        project: formData.linkType === 'project' ? formData.project : null,
        quotation: formData.linkType === 'quotation' ? formData.quotation : null,
        attachment: allAttachments
      };

      if (selectedRecord) {
        // ... (unchanged logic for changelog)
        const newChanges = [];
        if (selectedRecord.organization !== payload.organization) newChanges.push(`Organization changed`);
        if (selectedRecord.type !== payload.type) newChanges.push(`Type changed`);

        const changeEntry = {
          date: new Date().toISOString().split('T')[0],
          user: user?.name || 'Current User',
          action: 'Updated',
          changes: newChanges.join(', ') || 'Record updated'
        };

        const updatedChangelog = [...(selectedRecord.changelog || []), changeEntry];

        await recordService.update(selectedRecord.id, { ...payload, changelog: updatedChangelog });

        Swal.fire('Success', 'Record updated successfully!', 'success');
        setShowEditModal(false);
        setSelectedRecord(null);
      } else {
        // Create
        const creationLog = [{
          date: new Date().toISOString().split('T')[0],
          user: user?.name || payload.author,
          action: 'Created',
          changes: 'Initial record creation'
        }];

        const currentYear = new Date().getFullYear();
        const count = records.length + 1;
        const recordNo = `REC-${currentYear}-${String(count).padStart(3, '0')}`;

        await recordService.create({ ...payload, recordNo, changelog: creationLog });

        Swal.fire('Success', 'Record created successfully!', 'success');
        setShowModal(false);
      }
      loadRecords();
      // Reset form
      setFormData({
        date: '',
        organization: '',
        project: '',
        quotation: '',
        author: '',
        type: 'Letter',
        attachment: [],
        linkType: ''
      });

    } catch (error) {
      console.error("Submit error:", error);
      if (error.response) {
        console.error("Error Response Data:", error.response.data);
        console.error("Error Response Status:", error.response.status);
        console.error("Error Request URL:", error.config?.url);
      }
      if (selectedRecord) setShowEditModal(false); else setShowModal(false); // Close on error
      const errorMsg = error.response?.data?.error || 'Failed to save record';
      Swal.fire('Error', errorMsg, 'error');
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      date: record.date,
      organization: record.organization,
      project: record.project || '',
      quotation: record.quotation || '',
      author: record.author,
      type: record.type,
      attachment: Array.isArray(record.attachment) ? record.attachment : (record.attachment ? [record.attachment] : []),
      linkType: record.project ? 'project' : (record.quotation ? 'quotation' : '')
    });
    setShowEditModal(true);
  };

  const handleDelete = (recordId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await recordService.delete(recordId);
          setRecords(records.filter(r => r.id !== recordId));
          Swal.fire('Deleted!', 'Record has been deleted.', 'success');
        } catch (error) {
          console.error("Delete error:", error);
          Swal.fire('Error', 'Failed to delete record', 'error');
        }
      }
    });
  };

  const handleExport = (format) => {
    const filteredRecords = getFilteredRecords();
    if (format === 'excel') {
      // Create CSV content
      const headers = ['Record No.', 'Date', 'Organization', 'Project/Quotation', 'Author', 'Type'];
      const csvContent = [
        headers.join(','),
        ...filteredRecords.map(r => [
          r.recordNo,
          r.date,
          r.organization,
          r.project || r.quotation || '',
          r.author,
          r.type
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `records_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      Swal.fire('Success', 'Records exported to CSV!', 'success');
    } else if (format === 'pdf') {
      Swal.fire('Info', 'PDF export will be implemented with backend integration', 'info');
    }
  };

  const getFilteredRecords = () => {
    return records.filter(record => {
      const matchesSearch = !searchTerm ||
        record.recordNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.author.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = !filterType || record.type === filterType;
      const matchesOrg = !filterOrg || record.organization === filterOrg;

      return matchesSearch && matchesType && matchesOrg;
    });
  };

  const filteredRecords = getFilteredRecords();

  return (
    <div className="animate-fade-in">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            Record Management
          </h2>
          <p className="text-muted mb-0">Manage and track all your records efficiently</p>
        </div>
        <div>
          <button
            className="btn btn-success me-2"
            onClick={() => handleExport('excel')}
          >
            <i className="bi bi-file-earmark-excel"></i> Export Excel
          </button>
          <button
            className="btn btn-danger me-2"
            onClick={() => handleExport('pdf')}
          >
            <i className="bi bi-file-earmark-pdf"></i> Export PDF
          </button>
          {canManageRecords && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setSelectedRecord(null);
                setShowModal(true);
              }}
            >
              <i className="bi bi-plus-circle"></i> Add New Record
            </button>
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Search & Filter</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4 mb-3">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by Record No., Organization, Author..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Filter by Type</label>
              <select
                className="form-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="Letter">Letter</option>
                <option value="Quotation">Quotation</option>
                <option value="Invoice">Invoice</option>
                <option value="Contract">Contract</option>
              </select>
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Filter by Organization</label>
              <select
                className="form-select"
                value={filterOrg}
                onChange={(e) => setFilterOrg(e.target.value)}
              >
                <option value="">All Organizations</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.name}>{org.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">All Records</h5>
          <span className="badge bg-info">Total: {filteredRecords.length}</span>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Record No.</th>
                  <th>Date</th>
                  <th>Organization</th>
                  <th>Project/Quotation</th>
                  <th>Author</th>
                  <th>Type</th>
                  <th>Attachment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No records found
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record) => (
                    <tr key={record.id}>
                      <td><strong>{record.recordNo}</strong></td>
                      <td>{record.date}</td>
                      <td>{record.organization}</td>
                      <td>{record.project || record.quotation || '-'}</td>
                      <td>{record.author}</td>
                      <td>
                        <span className="badge bg-info">{record.type}</span>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {Array.isArray(record.attachment) && record.attachment.length > 0 ? (
                            record.attachment.map((file, idx) => (
                              <a
                                key={idx}
                                href={typeof file === 'string' ? `/uploads/records/${file}` : file.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="badge bg-light text-primary border text-decoration-none"
                                title={typeof file === 'string' ? file : file.name}
                              >
                                <i className="bi bi-file-earmark-text"></i>
                              </a>
                            ))
                          ) : '-'}
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-success me-2"
                          title="View Details"
                          onClick={() => {
                            setSelectedRecord(record);
                            setShowViewModal(true);
                          }}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        {canManageRecords && (
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(record)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-info me-2"
                          onClick={() => {
                            setSelectedRecord(record);
                            setShowChangelogModal(true);
                          }}
                        >
                          <i className="bi bi-clock-history"></i>
                        </button>
                        {canDelete && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(record.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Record</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ><i className="bi bi-x-lg"></i></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Organization</label>
                    <select
                      className="form-select"
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      required
                    >
                      <option value="">Select Organization</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.name}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Link To</label>
                    <select
                      className="form-select"
                      value={formData.linkType}
                      onChange={(e) => setFormData({ ...formData, linkType: e.target.value, project: '', quotation: '' })}
                    >
                      <option value="">None</option>
                      <option value="project">Project</option>
                      <option value="quotation">Quotation</option>
                    </select>
                  </div>
                  {formData.linkType === 'project' && (
                    <div className="mb-3">
                      <label className="form-label">Project</label>
                      <select
                        className="form-select"
                        value={formData.project}
                        onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                      >
                        <option value="">Select Project</option>
                        {projects.map(proj => (
                          <option key={proj.id} value={proj.title}>{proj.title}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {formData.linkType === 'quotation' && (
                    <div className="mb-3">
                      <label className="form-label">Quotation</label>
                      <select
                        className="form-select"
                        value={formData.quotation}
                        onChange={(e) => setFormData({ ...formData, quotation: e.target.value })}
                      >
                        <option value="">Select Quotation</option>
                        {quotations.map(quote => (
                          <option key={quote.id} value={quote.quoteNo}>{quote.quoteNo}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Author</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Record Type</label>
                    <select
                      className="form-select"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="Letter">Letter</option>
                      <option value="Quotation">Quotation</option>
                      <option value="Invoice">Invoice</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Attachments</label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      onChange={(e) => {
                        const newFiles = Array.from(e.target.files);
                        setFormData({ ...formData, attachment: [...formData.attachment, ...newFiles] });
                      }}
                    />
                    {formData.attachment.length > 0 && (
                      <div className="mt-2 d-flex flex-wrap gap-2">
                        {formData.attachment.map((file, idx) => (
                          <div key={idx} className="badge bg-light text-dark border p-2 d-flex align-items-center">
                            <i className="bi bi-paperclip me-1"></i>
                            <span className="text-truncate" style={{ maxWidth: '150px' }}>
                              {file instanceof File ? file.name : (typeof file === 'string' ? file : file.name)}
                            </span>
                            <button
                              type="button"
                              className="btn-close ms-2"
                              style={{ fontSize: '0.5rem' }}
                              onClick={() => {
                                const newAttachments = [...formData.attachment];
                                newAttachments.splice(idx, 1);
                                setFormData({ ...formData, attachment: newAttachments });
                              }}
                            ></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedRecord && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Record</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedRecord(null);
                  }}
                ><i className="bi bi-x-lg"></i></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Record No.</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedRecord.recordNo}
                      disabled
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Organization</label>
                    <select
                      className="form-select"
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      required
                    >
                      <option value="">Select Organization</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.name}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Link To</label>
                    <select
                      className="form-select"
                      value={formData.linkType}
                      onChange={(e) => setFormData({ ...formData, linkType: e.target.value, project: '', quotation: '' })}
                    >
                      <option value="">None</option>
                      <option value="project">Project</option>
                      <option value="quotation">Quotation</option>
                    </select>
                  </div>
                  {formData.linkType === 'project' && (
                    <div className="mb-3">
                      <label className="form-label">Project</label>
                      <select
                        className="form-select"
                        value={formData.project}
                        onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                      >
                        <option value="">Select Project</option>
                        {projects.map(proj => (
                          <option key={proj.id} value={proj.title}>{proj.title}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {formData.linkType === 'quotation' && (
                    <div className="mb-3">
                      <label className="form-label">Quotation</label>
                      <select
                        className="form-select"
                        value={formData.quotation}
                        onChange={(e) => setFormData({ ...formData, quotation: e.target.value })}
                      >
                        <option value="">Select Quotation</option>
                        {quotations.map(quote => (
                          <option key={quote.id} value={quote.quoteNo}>{quote.quoteNo}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Author</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Record Type</label>
                    <select
                      className="form-select"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="Letter">Letter</option>
                      <option value="Quotation">Quotation</option>
                      <option value="Invoice">Invoice</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Attachments</label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      onChange={(e) => {
                        const newFiles = Array.from(e.target.files);
                        setFormData({ ...formData, attachment: [...formData.attachment, ...newFiles] });
                      }}
                    />
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {formData.attachment.map((file, idx) => (
                        <div key={idx} className="badge bg-light text-dark border p-2 d-flex align-items-center">
                          <i className="bi bi-paperclip me-1"></i>
                          <span className="text-truncate" style={{ maxWidth: '150px' }}>
                            {file instanceof File ? file.name : (typeof file === 'string' ? file : file.name)}
                          </span>
                          <button
                            type="button"
                            className="btn-close ms-2"
                            style={{ fontSize: '0.5rem' }}
                            onClick={() => {
                              const newAttachments = [...formData.attachment];
                              newAttachments.splice(idx, 1);
                              setFormData({ ...formData, attachment: newAttachments });
                            }}
                          ></button>
                        </div>
                      ))}
                    </div>
                    <div className="form-text">Upload new files or remove existing ones</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedRecord(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showChangelogModal && selectedRecord && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Changelog - {selectedRecord.recordNo}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowChangelogModal(false);
                    setSelectedRecord(null);
                  }}
                ><i className="bi bi-x-lg"></i></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Action</th>
                        <th>Changes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRecord.changelog?.map((log, index) => (
                        <tr key={index}>
                          <td>{log.date}</td>
                          <td>{log.user}</td>
                          <td><span className="badge bg-info">{log.action}</span></td>
                          <td>{log.changes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowChangelogModal(false);
                    setSelectedRecord(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showViewModal && selectedRecord && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Record Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedRecord(null);
                  }}
                ><i className="bi bi-x-lg"></i></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12 pb-2 border-bottom">
                    <label className="text-muted small text-uppercase">Record No.</label>
                    <div className="fw-bold">{selectedRecord.recordNo}</div>
                  </div>
                  <div className="col-md-6 pb-2 border-bottom">
                    <label className="text-muted small text-uppercase">Date</label>
                    <div>{selectedRecord.date}</div>
                  </div>
                  <div className="col-md-6 pb-2 border-bottom">
                    <label className="text-muted small text-uppercase">Type</label>
                    <div><span className="badge bg-info">{selectedRecord.type}</span></div>
                  </div>
                  <div className="col-12 pb-2 border-bottom">
                    <label className="text-muted small text-uppercase">Organization</label>
                    <div>{selectedRecord.organization}</div>
                  </div>
                  <div className="col-12 pb-2 border-bottom">
                    <label className="text-muted small text-uppercase">Project/Quotation</label>
                    <div>{selectedRecord.project || selectedRecord.quotation || 'None'}</div>
                  </div>
                  <div className="col-12 pb-2 border-bottom">
                    <label className="text-muted small text-uppercase">Author</label>
                    <div>{selectedRecord.author}</div>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small text-uppercase">Attachments</label>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {Array.isArray(selectedRecord.attachment) && selectedRecord.attachment.length > 0 ? (
                        selectedRecord.attachment.map((file, idx) => (
                          <a
                            key={idx}
                            href={typeof file === 'string' ? `/uploads/records/${file}` : file.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary d-flex align-items-center"
                          >
                            <i className="bi bi-file-earmark-arrow-down me-1"></i>
                            <span className="text-truncate" style={{ maxWidth: '200px' }}>
                              {typeof file === 'string' ? file : file.name}
                            </span>
                          </a>
                        ))
                      ) : <div className="text-muted">No attachments</div>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedRecord(null);
                  }}
                >
                  Close
                </button>
                {canManageRecords && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowViewModal(false);
                      handleEdit(selectedRecord);
                    }}
                  >
                    Edit Record
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordManagement;
